import React, { useState, useEffect, useRef, useCallback } from 'react'
import { css } from '@emotion/core'
import { useLocation, Link, useQueryParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { useUser } from '@fs/zion-user'
import { useAtSize, IconButton, LabelTooltip } from '@fs/zion-ui'
import { boolAttr } from '@fs/zion-frontend-friends'
import { SocialComment } from '@fs/zion-icon'
import { trackLink } from '@fs/zion-analytics'
import Menu from './menu/Menu'
import logo from '../images/rootstechByFS.svg'
import searchSVG from '../images/nav/search.svg'
import homeSVG from '../images/nav/home.svg'
import playlistSVG from '../images/nav/playlist.svg'
import seriesSVG from '../images/nav/series.svg'
import speakersSVG from '../images/nav/speakers.svg'
import expohallSVG from '../images/nav/expohall.svg'
import { useChatOverlayContext } from './chat/ChatOverlayProvider'
import { useSupportPageContext } from './volunteer/VolunteerSupportContext'
import PillButton from './PillButton'
import { loginUserRTCCampaignID } from '../global/campainIds'
import { Chat } from '../global/chatSettingsConfig'

const tabsCss = css`
  margin-left: 32px;
  display: flex;
  flex-wrap: wrap;

  & a {
    font-size: 14px;
    height: 40px;
    display: flex;
    align-items: center;
    padding: 0;
    text-transform: none;
    transition: color 0.2s ease;
    position: relative;
    min-width: unset;
    margin: 0 16px;

    &::after {
      content: '';
      height: 4px;
      width: 0;
      background: #d83b95;
      opacity: 0.5;
      position: absolute;
      bottom: -4px;
      border-radius: 4px;
      left: 0;
      right: 0;
      margin: 0 auto;
      transition: 0.1s;
    }
  }

  & a:before {
    content: '';
    position: relative;
    height: 18px;
    width: 18px;
    left: -5px;
    background-size: 18px;
    background-repeat: none;
    display: inline-block;
  }
  & a[href$='/search']:before {
    background-image: url(${searchSVG});
  }
  & a[href$='/home']:before {
    background-image: url(${homeSVG});
  }
  & a[href$='/playlist']:before {
    background-image: url(${playlistSVG});
  }
  & a[href$='/series']:before {
    background-image: url(${seriesSVG});
  }
  & a[href$='/expohall']:before {
    background-image: url(${expohallSVG});
  }
  & a[href$='/speakers']:before {
    background-image: url(${speakersSVG});
  }
  a:hover,
  a:focus {
    color: #ffffff;
    background: none;
    &::after {
      width: 50%;
    }
  }
  & a[active] {
    border: none;
    color: #ffffff;
    &::after {
      width: 100%;
      opacity: 1;
    }
  }
`

const navBarCss = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 74px;
  padding: 8px 0;
`

const navBarRightCss = css`
  display: flex;
  align-items: center;
`

const navBarLogoCss = css`
  margin-left: 24px;
  &[is-mobile] {
    margin-left: 8px;
    & img {
      width: 70%;
    }
  }
`

const menuButtonWrapperCss = css`
  margin: 0 8px 0 16px;
  flex-shrink: 0; // prevent button from shrinking
  &[is-mobile] {
    margin: 0 8px 0 0;
  }
`

const auaButtonCss = css`
  margin-left: 24px;
  margin-right: 24px;
  &[is-mobile] {
    margin-right: 0;
  }
  & button {
    padding-top: 8px;
    height: 40px;
    width: 40px;
  }
  &[active] button {
    --icon-button-background: #2c8f9f;
    --icon-button-color: white;
  }
  & button[emphasis='medium'] {
    --icon-button-color: white;
  }
`

const signInCss = css`
  margin: 0 16px;
  & > a {
    padding: 0 16px !important;
    min-height: 32px !important;
  }
`

const hideNavWhenThesePaths = ['tempregister']
const tabPaths = ['/home', '/series', '/search', '/playlist', '/expohall', '/speakers']

export default function NavBar() {
  const location = useLocation()
  const [activeTabIndex, setActiveTab] = useState(0)
  const [auaActive, setAUAActive] = useState(false)
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ xxl: false, default: true })
  const isTablet = atSize({ lg: false, default: true })
  const user = useUser()
  const nextActiveIndex = tabPaths.indexOf(location.pathname)
  const { setIsOpen, isOpen } = useChatOverlayContext()
  const { setIsOpen: setSupportIsOpen } = useSupportPageContext()
  const isSupportPage = location.pathname === '/volunteer/support'
  const myPlaylistRef = useRef()

  useEffect(() => {
    window?.chatHistory?.listen((loc) => {
      if (loc.pathname.startsWith('/ask-us-anything')) {
        setAUAActive(true)
      } else if (auaActive && !loc.pathname.startsWith('/ask-us-anything')) {
        setAUAActive(false)
      }
    })
  }, [auaActive, isOpen, location.pathname])

  useEffect(() => {
    if (!isOpen) {
      setAUAActive(false)
    }
  }, [auaActive, isOpen, location.pathname])

  const handleAUAClick = useCallback(() => {
    if (isOpen && window.chatHistory.location.pathname.startsWith('/ask-us-anything')) {
      setIsOpen(false)
    } else {
      setIsOpen(true)
      if (isSupportPage) {
        setSupportIsOpen(true)
      } else if (i18n.language === 'en') {
        window.chatHistory.push('/ask-us-anything')
      } else {
        window.chatHistory.push(`/ask-us-anything/language/${i18n.language}`)
      }
    }
  }, [isOpen, isSupportPage, setIsOpen, setSupportIsOpen])

  const { query, removeQuery } = useQueryParams()
  useEffect(() => {
    if (query.aua) {
      setTimeout(() => {
        removeQuery('aua')
        handleAUAClick()
      }, 500)
    }
  }, [handleAUAClick, query.aua, removeQuery])

  if (activeTabIndex !== nextActiveIndex) {
    setActiveTab(nextActiveIndex)
  }

  if (hideNavWhenThesePaths.some((path) => location.pathname.includes(path))) {
    return null
  }

  return (
    <nav css={navBarCss}>
      <div css={navBarLogoCss} is-mobile={boolAttr(isMobile)}>
        <Link data-testid="rootstech-logo" to="/" aria-label={t('navigation.home.label', 'Home')}>
          <img src={logo} alt="" aria-hidden style={{ padding: '2px 0', height: '48px' }} />
        </Link>
      </div>

      <div css={navBarRightCss}>
        {!isMobile ? (
          <>
            <div css={tabsCss} is-tablet={boolAttr(isTablet)}>
              <Link noLinkStyles active={boolAttr(location.pathname === '/home')} data-testid="tab-home" to="/home">
                {t('navigation.home.label', 'Home')}
              </Link>
              <Link
                noLinkStyles
                active={boolAttr(location.pathname === '/series')}
                data-testid="tab-series"
                to="/series"
              >
                {t('navigation.series.label', 'Guide Me')}
              </Link>
              <Link
                noLinkStyles
                active={boolAttr(location.pathname === '/search')}
                data-testid="tab-search"
                to="/search"
              >
                {t('navigation.sessions.label', 'Sessions')}
              </Link>
              <Link
                noLinkStyles
                active={boolAttr(location.pathname === '/playlist')}
                data-testid="tab-my-playlist"
                linkRef={myPlaylistRef}
                to="/playlist"
              >
                {t('navigation.my-playlist.label', 'My Playlist')}
              </Link>
              <Link
                noLinkStyles
                active={boolAttr(location.pathname === '/expohall')}
                data-testid="tab-expo-hall"
                to="/expohall"
              >
                {t('navigation.expo-hall.label', 'Expo Hall')}
              </Link>
            </div>

            {Chat.Settings.ASK_EXPERT_ENABLED && (
              <LabelTooltip
                placement="bottom"
                label={t('chat.ask-us-anything', 'Ask Us Anything')}
                target={
                  <div css={auaButtonCss} active={boolAttr(auaActive)} is-mobile={boolAttr(isMobile)}>
                    <IconButton
                      aria-label={t('chat.ask-us-anything', 'Ask Us Anything')}
                      emphasis="high"
                      Icon={SocialComment}
                      onClick={handleAUAClick}
                    />
                  </div>
                }
              />
            )}

            {!user.signedIn && !user.userLoading && (
              <div css={signInCss}>
                <PillButton
                  data-testid="sign-in-NavBar"
                  to={`/auth/familysearch/login?returnUrl=${encodeURIComponent(
                    window.location.pathname
                  )}?cid=${loginUserRTCCampaignID}`}
                  external
                  emphasis="high"
                  onClick={() => {
                    trackLink(null, '/auth/familysearch/login', 'click_action', 'lo_hdr19_sign_in')
                  }}
                >
                  {t('common-ui:sign-in.action', 'Sign In')}
                </PillButton>
              </div>
            )}
          </>
        ) : (
          Chat.Settings.ASK_EXPERT_ENABLED && (
            <div css={auaButtonCss} active={boolAttr(auaActive)} is-mobile={boolAttr(isMobile)}>
              <IconButton emphasis={isTablet ? 'medium' : 'high'} Icon={SocialComment} onClick={handleAUAClick} />
            </div>
          )
        )}

        <div css={menuButtonWrapperCss} is-mobile={boolAttr(isMobile)}>
          <Menu showNavItems={isMobile} />
        </div>
      </div>
    </nav>
  )
}
